import { ButtonLooks, FontIcons, IconButton } from '@brandfolder/react';
import classNames from 'classnames';
import { FunctionComponent, useContext } from 'react';
import styled from 'styled-components';

import { AsideContext } from '@context/aside';
import { BlockContext } from '@context/blocks';
import { PagesContext } from '@context/pages';

import { OmittedAsideDialogProps } from './aside-dialog';
import { AsidePopout } from './aside-popout';
import { PagesPopout } from './pages/pages-popout';
import { PagesSettings } from './pages/pages-settings';

interface AsidePagesProps extends OmittedAsideDialogProps {}

const UnstyledAsidePages: FunctionComponent<AsidePagesProps> = (props) => {
  const { setShowPages, setShowPagesSettings, showPages, showPagesSettings } =
    useContext(AsideContext);
  const { initialBlocks, setBlocks } = useContext(BlockContext);
  const { editingPage, setEditingPage } = useContext(PagesContext);

  const renderBackArrow = () => {
    return (
      <IconButton
        className="back-arrow"
        icon={FontIcons.ArrowLeft}
        label="Back to pages list"
        look={ButtonLooks.Default}
        onClick={(): void => {
          setEditingPage(null);
          setShowPagesSettings(false);
        }}
      />
    );
  };

  const renderTitle = (): string => {
    if (showPagesSettings) return 'Pages & Sections Settings';
    if (editingPage?.key) return 'Edit Page';
    if (editingPage) return 'Add Page';
    return 'Pages & Sections';
  };

  return (
    <AsidePopout
      {...props}
      className={classNames({
        'aside-pages': true,
        'aside-pages--settings': showPagesSettings || !!editingPage,
        [`${props.className}`]: !!props.className
      })}
      content={showPagesSettings ? <PagesSettings /> : <PagesPopout />}
      height={showPagesSettings ? 568 : editingPage ? 371 : undefined}
      icon={FontIcons.RulesEngine}
      id="aside-pages"
      label="Open pages and sections"
      open={showPages}
      setOpen={setShowPages}
      titleBefore={(showPagesSettings && renderBackArrow()) || (editingPage && renderBackArrow())}
      title={renderTitle()}
      titleAfter={
        showPagesSettings || editingPage ? undefined : (
          <IconButton
            className={classNames({
              'aside-pages-settings-title-icon': true,
              'aside-pages-settings-show-settings': true
            })}
            icon={FontIcons.Settings}
            label="Show page settings"
            look={ButtonLooks.Default}
            onClick={() => {
              setShowPagesSettings(true);
            }}
          />
        )
      }
      tooltip="Pages"
      onClose={(): void => {
        setBlocks(initialBlocks);
        setShowPagesSettings(false);
      }}
    />
  );
};

const StyledAsidePages = styled(UnstyledAsidePages)`
  .aside-pages-settings-show-settings {
    margin-left: auto;
    min-height: 0;
    padding: 0;
  }

  &:not(.aside-pages--settings) {
    .bf-button__close {
      margin-left: 12px;
    }
  }

  .bf-switch__standard {
    width: 100%;

    + .bf-switch__standard {
      margin-top: 16px;
    }
  }
`;

export const AsidePages: FunctionComponent<AsidePagesProps> = (props) => {
  return <StyledAsidePages {...props} />;
};
